import { z } from 'zod';
import { ADJUSTMENT_REASON_KEYS, TENDER_METHODS, UNIT_KEYS, VAT_CATEGORY_KEYS } from './catalog.js';
import { CURRENCY_CODES } from './money.js';

/** Validation for Phase 2: catalogue, suppliers, purchasing, stock, sales and returns. */

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const cents = z.number().int().min(0).max(1_000_000_000_00);
const qty = z.number().int().positive('Quantity must be more than zero').max(1_000_000_000);
const signedQty = z
  .number()
  .int()
  .refine((v) => v !== 0, 'Quantity cannot be zero')
  .refine((v) => Math.abs(v) <= 1_000_000_000, 'Quantity too large');
const id = z.uuid();
const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-21')
  .nullable()
  .optional();

export const categorySchema = z.object({
  name: z.string().trim().min(2, 'Enter a category name').max(60),
  parentId: id.nullable().optional(),
});
export type CategoryInput = z.infer<typeof categorySchema>;

export const barcodeSchema = z
  .string()
  .trim()
  .min(4, 'Barcodes are at least 4 characters')
  .max(32)
  .regex(/^[0-9A-Za-z\-.]+$/, 'Letters, numbers, dots and dashes only');

export const productSchema = z.object({
  sku: z
    .string()
    .trim()
    .toUpperCase()
    .min(1, 'Enter a product code')
    .max(32)
    .regex(/^[A-Z0-9\-_.]+$/, 'Letters, numbers, dashes, dots, underscores'),
  name: z.string().trim().min(2, 'Enter the product name').max(120),
  description: nullableText(500),
  categoryId: id.nullable().optional(),
  unit: z.enum(UNIT_KEYS),
  priceCents: cents,
  costCents: cents.default(0),
  vatCategory: z.enum(VAT_CATEGORY_KEYS),
  reorderLevelMilli: z.number().int().min(0).max(1_000_000_000).default(0),
  trackStock: z.boolean().default(true),
  isActive: z.boolean().default(true),
  hsCode: z
    .string()
    .trim()
    .regex(/^(\d{4}|\d{8})?$/, 'ZIMRA accepts HS codes of 4 or 8 digits')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  barcodes: z.array(barcodeSchema).max(10).default([]),
  /** Phase 4 fields; ignored unless the related module is enabled. */
  tracksExpiry: z.boolean().default(false),
  pluCode: z
    .string()
    .trim()
    .regex(/^(\d{1,6})?$/, 'PLU codes are up to 6 digits')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});
export type ProductInput = z.infer<typeof productSchema>;

export const supplierSchema = z.object({
  name: z.string().trim().min(2, 'Enter the supplier name').max(120),
  contactName: nullableText(80),
  phone: nullableText(30),
  email: z
    .string()
    .trim()
    .max(120)
    .transform((v) => (v === '' ? null : v))
    .pipe(z.email('Enter a valid email').nullable())
    .nullable()
    .optional(),
  address: nullableText(200),
  tin: z
    .string()
    .trim()
    .regex(/^(\d{10})?$/, 'ZIMRA TIN is 10 digits')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  vatNumber: z
    .string()
    .trim()
    .regex(/^(\d{9})?$/, 'VAT number is 9 digits')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  paymentTermsDays: z.number().int().min(0).max(365).default(0),
  notes: nullableText(500),
  isActive: z.boolean().default(true),
});
export type SupplierInput = z.infer<typeof supplierSchema>;

export const purchaseOrderSchema = z.object({
  supplierId: id,
  branchId: id,
  expectedDate: dateOnly,
  notes: nullableText(500),
  lines: z
    .array(z.object({ productId: id, qtyMilli: qty, unitCostCents: cents }))
    .min(1, 'Add at least one product')
    .max(500),
});
export type PurchaseOrderInput = z.infer<typeof purchaseOrderSchema>;

export const goodsReceiptSchema = z.object({
  supplierId: id,
  branchId: id,
  purchaseOrderId: id.nullable().optional(),
  supplierInvoiceRef: nullableText(60),
  invoiceDate: dateOnly,
  /** Amount owed on the supplier's invoice (incl. VAT). Defaults to the sum of line costs. */
  invoiceTotalCents: cents.nullable().optional(),
  /** Phase 3: true when the invoice is to be paid later (creates a supplier balance). */
  onCredit: z.boolean().default(false),
  /** Phase 3: how it was paid when not on credit. */
  paidMethod: z.enum(['cash', 'card', 'ecocash', 'onemoney', 'innbucks', 'omari', 'bank_transfer']).nullable().optional(),
  notes: nullableText(500),
  lines: z
    .array(
      z.object({
        productId: id,
        purchaseOrderLineId: id.nullable().optional(),
        qtyMilli: qty,
        unitCostCents: cents,
        batchNo: nullableText(40),
        expiryDate: dateOnly,
      }),
    )
    .min(1, 'Add at least one product')
    .max(500),
});
export type GoodsReceiptInput = z.infer<typeof goodsReceiptSchema>;

export const adjustmentSchema = z.object({
  branchId: id,
  reason: z.enum(ADJUSTMENT_REASON_KEYS),
  note: nullableText(500),
  lines: z
    .array(
      z.object({
        productId: id,
        qtyChangeMilli: signedQty,
        unitCostCents: cents.nullable().optional(),
        /** Expiry-tracked products being added: the batch and its expiry date. */
        batchNo: nullableText(40),
        expiryDate: dateOnly,
      }),
    )
    .min(1, 'Add at least one product')
    .max(500),
});
export type AdjustmentInput = z.infer<typeof adjustmentSchema>;

export const transferSchema = z
  .object({
    fromBranchId: id,
    toBranchId: id,
    note: nullableText(500),
    lines: z.array(z.object({ productId: id, qtyMilli: qty })).min(1, 'Add at least one product').max(500),
  })
  .refine((v) => v.fromBranchId !== v.toBranchId, { message: 'Choose two different branches', path: ['toBranchId'] });
export type TransferInput = z.infer<typeof transferSchema>;

export const stocktakeCreateSchema = z.object({
  branchId: id,
  name: z.string().trim().min(2, 'Give the count a name').max(80),
  categoryId: id.nullable().optional(),
});

export const stocktakeCountsSchema = z.object({
  lines: z
    .array(z.object({ productId: id, countedMilli: z.number().int().min(0).max(1_000_000_000).nullable() }))
    .max(5000),
});

const discountSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('percent'), bp: z.number().int().min(1).max(10_000) }),
    z.object({ kind: z.literal('amount'), cents: z.number().int().min(1).max(1_000_000_000) }),
  ])
  .nullable()
  .optional();

export const paymentInputSchema = z.object({
  method: z.enum(TENDER_METHODS as [string, ...string[]]),
  currency: z.enum(CURRENCY_CODES as [string, ...string[]]),
  /** Amount handed over, in the payment's currency (cash may exceed what is due; change is given). */
  amountCents: z.number().int().positive('Enter an amount').max(1_000_000_000),
  reference: nullableText(60),
});
export type PaymentInput = z.infer<typeof paymentInputSchema>;

export const saleSchema = z.object({
  /** Random id generated by the till; repeating it returns the original sale instead of selling twice. */
  clientRef: z.uuid(),
  branchId: id,
  customerId: id.nullable().optional(),
  lines: z
    .array(
      z.object({
        productId: id,
        qtyMilli: qty,
        /** Only with price-override permission. */
        unitPriceCents: cents.nullable().optional(),
        manualDiscount: discountSchema,
      }),
    )
    .min(1, 'The sale has no items')
    .max(300),
  saleDiscount: discountSchema,
  payments: z.array(paymentInputSchema).max(10),
  /** Currency the change is handed back in (defaults to base currency). */
  changeCurrency: z.enum(CURRENCY_CODES as [string, ...string[]]).nullable().optional(),
  note: nullableText(300),
});
export type SaleInput = z.infer<typeof saleSchema>;

export const quoteSchema = saleSchema.pick({ branchId: true, lines: true, saleDiscount: true, customerId: true });

export const returnSchema = z.object({
  saleId: id,
  reason: z.string().trim().min(3, 'Say why the goods came back').max(200),
  refundMethod: z.enum(['cash', 'card', 'ecocash', 'onemoney', 'innbucks', 'omari', 'bank_transfer', 'account']),
  refundCurrency: z.enum(CURRENCY_CODES as [string, ...string[]]).nullable().optional(),
  reference: nullableText(60),
  lines: z
    .array(z.object({ saleLineId: id, qtyMilli: qty, restock: z.boolean() }))
    .min(1, 'Choose at least one item to return')
    .max(300),
});
export type ReturnInput = z.infer<typeof returnSchema>;

export const voidSchema = z.object({ reason: z.string().trim().min(3, 'Say why the sale is being voided').max(200) });
